import { createContext } from 'preact';
import { useContext, useEffect, useMemo, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { useLocation } from 'preact-iso';
import en from './locales/en.json';
import de from './locales/de.json';
import fr from './locales/fr.json';
import it from './locales/it.json';

export const LOCALES = ['en', 'de', 'fr', 'it'] as const;
export type Locale = (typeof LOCALES)[number];

type Dict = typeof en;

const DICTS: Record<Locale, Dict> = { en, de, fr, it };

const COOKIE = 'bc_lang';
const STORAGE = 'bc_lang';

function isLocale(s: string | undefined): s is Locale {
  return !!s && (LOCALES as readonly string[]).includes(s);
}

function localeFromPath(path: string): Locale | null {
  const seg = path.split('/').filter(Boolean)[0];
  return isLocale(seg) ? (seg as Locale) : null;
}

function detectLocale(): Locale {
  if (typeof window === 'undefined') return 'en';
  const fromStorage = localStorage.getItem(STORAGE);
  if (isLocale(fromStorage ?? undefined)) return fromStorage as Locale;
  const fromCookie = document.cookie
    .split(';')
    .map((c) => c.trim())
    .find((c) => c.startsWith(COOKIE + '='))
    ?.slice(COOKIE.length + 1);
  if (isLocale(fromCookie)) return fromCookie as Locale;
  const nav = (navigator.language || 'en').slice(0, 2).toLowerCase();
  return isLocale(nav) ? (nav as Locale) : 'en';
}

function persistLocale(l: Locale) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE, l);
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${COOKIE}=${l}; path=/; max-age=${60 * 60 * 24 * 365}; SameSite=Lax${secure}`;
  document.documentElement.lang = l;
}

type I18nCtx = {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (path: string) => string;
  dict: Dict;
  localePath: (suffix?: string) => string;
};

const Ctx = createContext<I18nCtx>({
  locale: 'en',
  setLocale: () => {},
  t: (p) => p,
  dict: en,
  localePath: (s) => s ?? '/',
});

export function I18nProvider({ children }: { children: ComponentChildren }) {
  const loc = useLocation();
  const pathLocale = localeFromPath(loc.path);

  const [locale, setLocaleState] = useState<Locale>(pathLocale ?? detectLocale());

  useEffect(() => {
    if (pathLocale) {
      if (pathLocale !== locale) setLocaleState(pathLocale);
      persistLocale(pathLocale);
      return;
    }
    // Backend OAuth / magic-link redirects land at /auth/callback with a token.
    // Let that page do its own work, then it will navigate to a locale-prefixed root.
    if (loc.path.startsWith('/auth/callback')) return;
    const detected = detectLocale();
    const rest = loc.path === '/' ? '' : loc.path;
    loc.route(`/${detected}${rest}`, true);
  }, [pathLocale]);

  const value = useMemo<I18nCtx>(() => {
    const dict = DICTS[locale];
    const t = (path: string): string => {
      const segments = path.split('.');
      let cur: unknown = dict;
      for (const s of segments) {
        if (cur && typeof cur === 'object' && s in (cur as Record<string, unknown>)) {
          cur = (cur as Record<string, unknown>)[s];
        } else {
          return path;
        }
      }
      return typeof cur === 'string' ? cur : path;
    };
    return {
      locale,
      setLocale: (l) => {
        const segs = loc.path.split('/').filter(Boolean);
        if (isLocale(segs[0])) segs[0] = l;
        else segs.unshift(l);
        loc.route('/' + segs.join('/'), false);
      },
      t,
      dict,
      localePath: (suffix = '') => `/${locale}${suffix}`,
    };
  }, [locale, loc.path]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n() {
  return useContext(Ctx);
}
